// Centralized type definitions for the entire application
// This prevents duplication and ensures type consistency

export interface App {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
}

export interface Studio {
  id: string;
  title: string;
  subtitle: string;
  headerIcon: string;
  apps: App[];
}

export interface NavigationItem {
  id: string;
  name: string;
  icon: string;
  active: boolean;
  route: string;
}

// Component prop types for better reusability
export interface AppIconProps {
  name: string;
  color: string;
  size?: number;
}

export interface StudioSectionProps {
  title: string;
  subtitle: string;
  apps: App[];
  headerIcon: string;
}

export interface SidebarNavigationProps {
  items: NavigationItem[];
  onNavigate: (itemId: string, item: NavigationItem) => void;
}

export interface HomeProps {
  content: HomeContent;
}

// Home section data structure
export interface SuggestionChip {
  id: string;
  emoji: string;
  text: string;
}

export interface HomeContent {
  title: {
    main: string;
    highlight: string;
  };
  subtitle: string;
  promptPlaceholder: string;
  suggestions: SuggestionChip[];
}

export const appsData = {
  // Studio sections in an array format for easy mapping
  studios: [
    {
      id: "design-studio",
      title: "Design Studio",
      subtitle: "Ideas to ready-to-make designs",
      headerIcon: "design-studio",
      apps: [
        {
          id: "scout",
          name: "Scout",
          description: "Track, search, and adapt designs from across the web",
          icon: "scout",
          color: "#e55c58",
        },
        {
          id: "dreamboard",
          name: "Dreamboard",
          description: "A limitless canvas for your imagination",
          icon: "dreamboard",
          color: "#e55c58",
        },
        {
          id: "techpacks",
          name: "Techpacks",
          description: "Effortlessly manage and create techpacks with speed",
          icon: "techpacks",
          color: "#e55c58",
        },
      ],
    },
    {
      id: "photo-studio",
      title: "Photo Studio",
      subtitle: "Craft standout visuals and creatives",
      headerIcon: "photo-studio",
      apps: [
        {
          id: "on-model",
          name: "On-Model",
          description: "Swap model photos to professional models and scenes",
          icon: "on-model",
          color: "#6e56cf",
        },
        {
          id: "mannequin",
          name: "Mannequin",
          description:
            "Swap mannequin photos to professional models and scenes",
          icon: "mannequin",
          color: "#6e56cf",
        },
        {
          id: "product",
          name: "Product",
          description: "Create studio quality visuals from product photos",
          icon: "product",
          color: "#6e56cf",
        },
        {
          id: "flat-lay",
          name: "Flat - Lay",
          description:
            "Transform flat lay photos to professional on-model shoots",
          icon: "flat-lay",
          color: "#6e56cf",
        },
        {
          id: "static-ads",
          name: "Static Ads",
          description: "Create professional banners and ads in moments",
          icon: "static-ads",
          color: "#6e56cf",
        },
        {
          id: "style-studio",
          name: "Style Studio",
          description:
            "A 3D playground for complete control of product and shoot styling",
          icon: "style-studio",
          color: "#6e56cf",
        },
      ],
    },
    {
      id: "catalog-studio",
      title: "Catalog Studio",
      subtitle: "Manage content on all online storefronts",
      headerIcon: "catalog-studio",
      apps: [
        {
          id: "stores",
          name: "Stores",
          description:
            "Centralized product and asset management for every storefront",
          icon: "stores",
          color: "#6672fb",
        },
        {
          id: "batches",
          name: "Batches",
          description: "Merge, transform, and validate product data in batches",
          icon: "batches",
          color: "#6672fb",
        },
        {
          id: "listings",
          name: "Listings",
          description:
            "Automatically list and track products on all marketplaces",
          icon: "listings",
          color: "#6672fb",
        },
        {
          id: "ai-training",
          name: "AI Training",
          description:
            "Train tailored models on your data for superior accuracy",
          icon: "ai-training",
          color: "#6672fb",
        },
        {
          id: "insights",
          name: "Insights",
          description: "Monitor team performance and analyze product data",
          icon: "insights",
          color: "#6672fb",
        },
      ],
    },
  ] as Studio[],
  // Home content data
  homeContent: {
    title: {
      main: "What do you want to ",
      highlight: "design?",
    },
    subtitle: "From your concept to commerce — I'm here to help, step by step.",
    promptPlaceholder: "What's your concept? Type it out...",
    suggestions: [
      {
        id: "winter-collection",
        emoji: "❄",
        text: "Bold & Cozy S25–26 Winter Collection",
      },
      {
        id: "summer-shorts",
        emoji: "🩳",
        text: "Design summer-ready shorts for men",
      },
      {
        id: "everyday-wear",
        emoji: "👜",
        text: "Build a minimal everyday wear collection",
      },
      {
        id: "workwear",
        emoji: "💼",
        text: "Design a modern workwear capsule",
      },
      {
        id: "y2k-outfit",
        emoji: "🕶️",
        text: "Create a bold Y2K-inspired outfit",
      },
    ],
  } as HomeContent,

  sidebarNavigation: [
    // {
    //   id: "home",
    //   name: "Home",
    //   icon: "home",
    //   active: true,
    //   route: "home",
    // },
    {
      id: "teams",
      name: "Teams",
      icon: "teams",
      active: false,
      route: "teams",
    },
    {
      id: "workspace",
      name: "Workspaces",
      icon: "apps",
      active: false,
      route: "workspace",
    },
    {
      id: "photogenix",
      name: "Photogenix",
      icon: "library",
      active: false,
      route: "photogenix",
    },
    {
      id: "services",
      name: "Services",
      icon: "gear-icon-black",
      active: false,
      route: "service",
    },
    {
      id: "subscriptions",
      name: "Subscriptions",
      icon: "billing",
      active: false,
      route: "subscriptions",
    },
    {
      id: "user-specializations",
      name: "User Specialization",
      icon: "library",
      active: false,
      route: "user-specializations",
    },
    {
      id: "stripe-products",
      name: "Stripe Products",
      icon: "product",
      active: false,
      route: "stripe-products",
    },
    {
      id: "paid-users",
      name: "Paid Users",
      icon: "teams",
      active: false,
      route: "paid-users",
    },
    {
      id: "billing-alerts",
      name: "Billing Alerts",
      icon: "billing",
      active: false,
      route: "billing-alerts",
    },
    {
      id: "ai-gateway",
      name: "AI Gateway",
      icon: "insights",
      active: false,
      route: "ai-gateway",
    },
    {
      id: "organizations",
      name: "Organizations",
      icon: "teams",
      active: false,
      route: "organizations",
    },
    {
      id: "workspace-org-migration",
      name: "Org Migration",
      icon: "apps",
      active: false,
      route: "workspace-org-migration",
    },
    {
      id: "data-cleanup",
      name: "Data Cleanup",
      icon: "gear-icon-black",
      active: false,
      route: "data-cleanup",
    },
    // {
    //   id: "billing",
    //   name: "Billing",
    //   icon: "billing",
    //   active: false,
    //   route: "billing",
    // },
  ] as NavigationItem[],
};
